/**
 * renderCache.js — when a render can be reused, decided by what it depicts.
 *
 * A full render of a real case takes about 110 s, and captions, annotations or
 * page ORDER change no pixel of it. So the Builder reuses a render whenever the
 * pictures would be identical. Everything here is about deciding that honestly.
 *
 * ── Two defects this replaces ─────────────────────────────────────────────
 *
 * 1. The key named the VIEWS each scene needs via `viewsForScenes(doc)` — which
 *    returns a Map, and JSON.stringify writes every Map as `{}`. The views plan
 *    was never part of the key. Measured: adding the Source data page asks the
 *    Native Skull scene for one more view (ISO) while every scene stays the
 *    same, and the key did not change — the cached render, which never drew
 *    ISO, was served and the page got an empty picture box. Reachable today
 *    whenever scan metadata arrives after a build; routine in page mode, where
 *    adding a page is the normal act.
 *
 * 2. The key named scenes by ID. Ids are handed out in draft order, so in page
 *    mode a pure REORDER renumbers every scene, misses the cache and re-renders
 *    the whole case for pictures that are byte-identical.
 *
 * So a scene is named by its CONTENT — its name, fit group, frame and every
 * part's appearance — with the sorted views it needs, and the whole list is
 * sorted, so order stops mattering and the views plan starts to. The cached
 * pictures are stored by content too, and handed back under whatever ids the
 * current draft gave the same scenes.
 *
 * Membership of a fit group is inside the key by construction: every scene's
 * fit group is listed, so adding or removing a member — which can change the
 * shared scale of the others — misses the cache, as it must.
 */
import { renderScenes, viewsForScenes } from './renderScenes.js'

/** One scene's identity by what it depicts, independent of its id. */
export function sceneContent(scene, parts = []) {
  return JSON.stringify([
    scene.name ?? null,
    // A scene with no group of its own gets its ID as its group (schema.js
    // newScene), so the raw value would smuggle the id back in and a reorder
    // would miss the cache again — found by the reorder test. A group of one is
    // framed by itself alone, which is exactly what `(self)` says.
    scene.fitGroup === scene.id ? '(self)' : (scene.fitGroup ?? null),
    scene.frame ?? null,
    // EVERY field that reaches the renderer: `neutral` and the per-polygroup
    // maps change pixels without changing the colour.
    parts.map((p) => [
      p.meshId, p.name, p.color ?? null, p.neutral === true, p.groups ?? null, p.neutralGroups ?? null,
    ]),
  ])
}

/**
 * The key a render is valid for.
 *
 * @param {object} a
 * @param {object} a.doc         the document being built (its scenes and pages)
 * @param {object} a.sceneParts  sceneId → parts, from the draft
 * @param {Array}  a.library     the classified files — identity AND role/stage
 * @param {object} a.renderOpts  the look; it changes every pixel
 */
export function renderCacheKey({ doc, sceneParts, library, renderOpts }) {
  const wanted = viewsForScenes(doc)
  const scenes = (doc.scenes ?? [])
    .map((s) => [sceneContent(s, sceneParts[s.id]), [...(wanted.get(s.id) ?? [])].sort()])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
  return JSON.stringify([
    // The actual FILES: two cases can hold identically named meshes, and ids
    // derive from names, so without size and mtime a second case would reuse
    // the first one's pictures under its own patient's name. Role and stage are
    // here because they decide colour and which pages exist while the file's
    // identity stays exactly the same.
    [...library].map((m) => [
      m.id, m.name, m.role, m.stage, m.handle?.size ?? null, m.handle?.lastModified ?? null,
    ]).sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)),
    scenes,
    renderOpts ?? null,
  ])
}

/**
 * Render a document, or reuse a previous render of the same pictures.
 *
 * @returns {Promise<{views: object, reliefWarnings: string[], cache: object, reused: boolean}>}
 *   `views` is sceneId → { view: dataUrl } under THIS draft's ids. Pass `cache`
 *   back in next time.
 */
export async function renderWithCache({
  doc, sceneParts, library, renderOpts, cache = null, ...renderArgs
}) {
  const key = renderCacheKey({ doc, sceneParts, library, renderOpts })
  if (cache?.key === key) {
    const views = {}
    for (const s of doc.scenes ?? []) views[s.id] = cache.byContent[sceneContent(s, sceneParts[s.id])]
    return { views, reliefWarnings: cache.reliefWarnings, cache, reused: true }
  }
  const rendered = await renderScenes(doc, sceneParts, { ...renderArgs, renderOpts })
  const byContent = {}
  for (const s of doc.scenes ?? []) byContent[sceneContent(s, sceneParts[s.id])] = rendered.views[s.id]
  const next = { key, byContent, reliefWarnings: rendered.reliefWarnings ?? [] }
  return { views: rendered.views, reliefWarnings: next.reliefWarnings, cache: next, reused: false }
}
